/* eslint-disable react/jsx-no-undef */
import { BrowserRouter, Route, Routes } from "react-router-dom";

import { AdmissionApply } from "./features/admission/pages/AdmissionApply";
import { AdmissionVerify } from "./features/admission/pages/AdmissionVerify";
import { Enquiry } from "./features/admission/pages/Enquiry";
import { ChangePassword } from "./modules/admin/ChangePassword";
import { Promote } from "./modules/admin/Promote";
import { Attendance } from "./modules/common/components/Attendance/Attendance";
import { Enquiries } from "./modules/common/components/Enquiries";
import { User } from "./modules/common/components/user/User";
import { NoPage } from "./NoPage";
import { ProtectedRoute } from "./ProtectedRoute";
import { LogoutPage } from "./pages/LogoutPage";
import { SessionExpiredPage } from "./pages/SessionExpiredPage";
import StudentAddPage from "./features/student/pages/StudentAddPage";
import { StudentsList } from "./features/student/pages/StudentsList";

import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import { PaymentStatus } from "./modules/common/components/payment/PaymentStatus";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useEffect } from "react";
import { useConfgrAxiosInterceptors } from "./api/axiosInstance";
import { AuthProvider } from "./auth/AuthContext";
import { AcademicYearContextProvider, useAcademicYearContext } from "./context/AacademicYearContext";
import { LoadingProvider, useLoading } from "./context/loading/LoadingContext";
import { NotificationProvider } from "./context/NotificationContext";
import YearTransitionWizard from "./features/academicOperations/components/yearTransition/YearTransitionWizard";
import AcademicsSetupPage from "./features/academicOperations/pages/AcademicsSetupPage";
import TimetablePage from "./features/academicOperations/pages/TimetablePage";
import MyTimetablePage from "./features/academicOperations/pages/MyTimetablePage";
import MyLeavePage from "./features/leave/pages/MyLeavePage";
import LeaveRequestsPage from "./features/leave/pages/LeaveRequestsPage";
import SubstitutionsPage from "./features/substitution/pages/SubstitutionsPage";
import MySubstitutionsPage from "./features/substitution/pages/MySubstitutionsPage";
import SystemSettingsPage from "./pages/app/SystemSettingsPage";
import FacultyForm from "./features/faculty/components/FacultyForm";
import StaffDirectoryPage from "./features/staff/pages/StaffDirectoryPage";
import { FeeDuesC } from "./features/feeDue/pages/FeeDues";
import { FeeFillingForm } from "./features/fees/components/FeeFillingForm";
import { PaymentSuccess } from "./features/fees/pages/PaymentSuccess";
import StaffAddPage from "./features/staff/pages/StaffAddPage";
import PersonAddForm from "./features/persons/components/PersonForm";
import PersonList from "./features/persons/components/PersonList";
import StudentImportPage from "./features/student/import/StudentImportPage";
import { AppLayout } from "./layouts/AppLayout";
import { PublicLayout } from "./layouts/PublicLayout";
import { AttendanceReport } from "./modules/common/components/Attendance/AttendanceReport";
import AttendanceTabs from "./modules/common/components/Attendance/FacultyAttendance";
import SendAlertPage from "./features/alerts/pages/SendAlertPage";
import { UserForm } from "./modules/common/components/user/UserForm";
import LoadingDialog from "./modules/common/shared/util/LoadingDialog";
import { StudentDetails } from "./modules/student/StudentDetails";
import { Dashboard } from "./pages/app/DashboardPage";
import { StudentDashboard } from "./features/dashboard/student/StudentDashboard";
import { RoleProtectedRoute } from "./RoleProtectedRoute";
import { PermissionRoute } from "./auth/PermissionRoute";
import { Permission } from "./auth/permissions";
import LandingPage from "./pages/LandingPage";
import { AppScrollbarStyles } from "./components/common/AppScrollbarStyles";
import { AppSurfaceStyles } from "./components/common/AppSurfaceStyles";
import { LoginPage } from "./pages/LoginPage";
import { ScrollToHash } from "./utils/ScrollToHash";
import { FeeReceiptsC } from "./features/fees/pages/FeeReceiptsC";
import FeeStructuresPage from "./features/fees/pages/FeeStructuresPage";
import { ReceiptPrintHost } from "./features/fees/receipt/ReceiptPrintHost";
import SettingsPage from "./features/settings/pages/SettingsPage";
import SchoolProfileSection from "./features/settings/schoolProfile/SchoolProfileSection";
import ReceiptPreviewSection from "./features/settings/receipts/ReceiptPreviewSection";
import { PreviousYearDuesPage } from "./features/feeDue/pages/PreviousYearDuesPage";
import { PaymentsHistory } from "./features/fees/pages/PaymentsHistory";
import { StudentTotalSummaryPage } from "./features/student/pages/StudentTotalSummaryPage";

const AppContent: React.FC = () => {
  const loading = useLoading();
  console.log("In AppContent: React.FC");
  useEffect(() => {
  }, []);

  return (
    <>
      <LoadingDialog open={loading.loading} />
      <ReceiptPrintHost />

      <Routes>

        <Route path="logout" element={<LogoutPage />} />
        <Route path="session-expired" element={<SessionExpiredPage />} />

        {/* TODO Remove unwanted routes */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<LandingPage />} />
          <Route path="login" element={<LoginPage />} />
          <Route path="enquiry/create" element={<Enquiry />} />
          <Route path="enquiry/verify" element={<AdmissionVerify />} />
          <Route path="admission/create" element={<AdmissionApply />} />
        </Route>

        <Route
          path="/app"
          element={<ProtectedRoute><AppLayout /></ProtectedRoute>}
        >
          <Route path="dashboard" element={<Dashboard />} />

          {/* URL convention: /app/<area> = list, /app/<area>/new = add, /app/<area>/:id = one record. */}

          {/* Fees */}
          <Route
            path="fees/previous-year-dues"
            element={
              <RoleProtectedRoute allowedRoles={["ROLE_ADMIN"]}>
                <PreviousYearDuesPage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="fees/summary"
            element={
              <RoleProtectedRoute allowedRoles={["ROLE_ADMIN"]}>
                <StudentTotalSummaryPage />
              </RoleProtectedRoute>
            }
          />
          <Route element={<PermissionRoute permission={Permission.FEES_VIEW} />}>
            <Route path="fees/outstanding" element={<FeeDuesC />} />
          </Route>
          <Route element={<PermissionRoute permission={Permission.FEES_RECEIPTS_VIEW} />}>
            <Route path="fees/receipts" element={<FeeReceiptsC />} />
          </Route>
          <Route element={<PermissionRoute permission={Permission.CONFIG_FEE_STRUCTURE_MANAGE} />}>
            <Route path="fees/structures" element={<FeeStructuresPage />} />
          </Route>
          <Route path="fees/pay" element={<FeeFillingForm />} />
          <Route path="fees/pay/success" element={<PaymentSuccess />} />
          <Route
            path="fees/payments"
            element={
              <RoleProtectedRoute allowedRoles={["ROLE_STUDENT"]}>
                <PaymentsHistory />
              </RoleProtectedRoute>
            }
          />
          {/* payment gateway return pages - the bank redirects here, keep these paths */}
          <Route path="payment/success/:poId" element={<PaymentStatus view="CHARGED" />} />
          <Route path="payment/backpressed/:poId" element={<PaymentStatus view="backpressed" />} />
          <Route path="payment/pending/:poId" element={<PaymentStatus view="PENDING" />} />
          <Route path="payment/unknown/:poId" element={<PaymentStatus view="unknown" />} />

          {/* Routes below are grouped by the same Permission their menuConfig item uses,
              so typing the URL directly is blocked just like the hidden menu link. */}
          <Route element={<PermissionRoute permission={Permission.STUDENTS_MANAGE} />}>
            <Route path="enquiries" element={<Enquiries />} />
            <Route path="students" element={<StudentsList />} />
            <Route path="students/new" element={<StudentAddPage />} />
            <Route path="students/import" element={<StudentImportPage />} />
            <Route path="students/promote" element={<Promote />} />
            <Route path="students/tc" element={<Promote />} />
            {/* the student comes in navigation state from the list; :id is the external id */}
            <Route path="students/:id" element={<StudentDetails />} />
          </Route>

          <Route element={<PermissionRoute permission={Permission.PERSONS_MANAGE} />}>
            <Route path="students/guardians" element={<PersonList />} />
            <Route path="students/guardians/new" element={<PersonAddForm />} />
          </Route>

          <Route element={<PermissionRoute permission={Permission.STUDENTS_ATTENDANCE_MARK} />}>
            <Route path="attendance/students" element={<Attendance />} />
          </Route>
          <Route element={<PermissionRoute permission={Permission.STAFF_ATTENDANCE_MARK} />}>
            <Route path="attendance/staff" element={<AttendanceTabs />} />
          </Route>
          <Route path="attendance/reports" element={<AttendanceReport />} />

          {/* Staff: Faculty (default) and support staff, switched by ?type=faculty|staff */}
          <Route element={<PermissionRoute permission={Permission.STAFF_MANAGE} />}>
            <Route path="staff" element={<StaffDirectoryPage />} />
            <Route path="staff/new" element={<StaffAddPage />} />
          </Route>
          <Route element={<PermissionRoute permission={Permission.FACULTY_MANAGE} />}>
            <Route path="staff/faculty/:id" element={<FacultyForm />} />
          </Route>

          <Route element={<PermissionRoute permission={Permission.ALERTS_MANAGE} />}>
            <Route path="notifications/send" element={<SendAlertPage />} />
          </Route>

          <Route element={<PermissionRoute permission={Permission.CONFIG_ACADEMICS_MANAGE} />}>
            {/* Academics → Setup: Academic Years, Sections, Grades, Subjects, Class Subjects as tabs */}
            <Route path="academics/setup" element={<AcademicsSetupPage />} />
            <Route path="academics/year-transition" element={<YearTransitionWizard />} />
          </Route>

          <Route element={<PermissionRoute permission={Permission.TIMETABLE_MANAGE} />}>
            <Route path="academics/timetable" element={<TimetablePage />} />
          </Route>

          <Route element={<PermissionRoute permission={Permission.TIMETABLE_VIEW_OWN} />}>
            <Route path="my-timetable" element={<MyTimetablePage />} />
          </Route>

          <Route element={<PermissionRoute permission={Permission.LEAVE_APPLY_OWN} />}>
            <Route path="my-leave" element={<MyLeavePage />} />
          </Route>

          <Route element={<PermissionRoute permission={Permission.LEAVE_MANAGE} />}>
            <Route path="staff/leave" element={<LeaveRequestsPage />} />
          </Route>

          <Route element={<PermissionRoute permission={Permission.SUBSTITUTIONS_MANAGE} />}>
            <Route path="staff/substitutions" element={<SubstitutionsPage />} />
          </Route>

          <Route element={<PermissionRoute permission={Permission.SUBSTITUTIONS_VIEW_OWN} />}>
            <Route path="my-substitutions" element={<MySubstitutionsPage />} />
          </Route>

          <Route element={<PermissionRoute permission={Permission.USERS_MANAGE} />}>
            <Route path="users" element={<User operation="CREATE" />} />
            <Route path="users/new" element={<UserForm />} />
          </Route>

          {/* Settings: one page, a side navigation of sections (features/settings/settingsSections.ts) */}
          <Route element={<PermissionRoute permission={Permission.CONFIG_SCHOOL_MANAGE} />}>
            <Route path="settings" element={<SettingsPage />}>
              <Route path="school" element={<SchoolProfileSection />} />
              <Route path="receipts" element={<ReceiptPreviewSection />} />
              <Route element={<PermissionRoute permission={Permission.CONFIG_SYSTEM_MANAGE} />}>
                <Route path="system" element={<SystemSettingsPage embedded />} />
              </Route>
            </Route>
          </Route>

          {/* every logged-in role */}
          <Route path="account/password" element={<ChangePassword />} />
        </Route>

        <Route path="*" element={<NoPage />} />
      </Routes>
    </>
  );
};

const AppGate = () => {
  useConfgrAxiosInterceptors();

  return <AppContent />;
};

function App() {

  return (
    <>
      <AppScrollbarStyles />
      <AppSurfaceStyles />
      <LoadingProvider>
        <AuthProvider>
          <AcademicYearContextProvider>
            <NotificationProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <BrowserRouter>
                  <ScrollToHash />
                  <AppGate />
                </BrowserRouter>
              </LocalizationProvider>
            </NotificationProvider>
          </AcademicYearContextProvider>
        </AuthProvider>
      </LoadingProvider>
    </>
  );
}

export default App;
